/** @typedef {import('@material-ui/core').ButtonProps} ButtonProps */
/** @typedef {import('react').ReactNode} ReactNode */

import { cloneElement } from 'react';
import { Button } from '@material-ui/core';
import PropTypes from 'prop-types';

/**
 * Small HOC wrapper over the material-ui button component to provide a consistent L&F for
 * buttons used in forms. Also provides functionality to wrap the button in a parent container
 * for sizing/positioning
 *
 * The button returned by this component has the following properties defaulted, which can be overridden via props:
 * - variant: `"contained"`
 * - color: `"secondary"`
 * - size: `"small"`
 *
 * Containers can be used via:
 *
 *     <NgpFormButton
 *         container={<Box mt={1} />}
 *         ...
 *         >
 *          <Trans>My Button</Trans>
 *     </NgpFormButton>
 *
 * @param {ButtonProps & { container?: ReactNode }} props
 * @returns {ReactElement}
 */
export default function NgpFormButton(props) {
    const { children, container, ...rest } = props;

    const button = (
        <Button variant="contained" color="secondary" size="small" {...rest}>
            {children}
        </Button>
    );

    if (container) {
        // we need to "inject" the button into the container that we were provided
        return cloneElement(container, null, button);
    }

    return button;
}

NgpFormButton.propTypes = {
    container: PropTypes.element,
};

NgpFormButton.defaultProps = {
    container: undefined,
};
